import React, { useState, useMemo } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { useToast } from '../context/ToastContext';

export default function ResetPasswordPage() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { addToast } = useToast();

  const token = useMemo(() => searchParams.get('token') || '', [searchParams]);
  const email = useMemo(() => searchParams.get('email') || '', [searchParams]);

  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!newPassword || !confirmPassword) {
      addToast('Please fill in both fields.', 'error');
      return;
    }
    if (newPassword.length < 8) {
      addToast('Password must be at least 8 characters long.', 'error');
      return;
    }
    if (newPassword !== confirmPassword) {
      addToast('Passwords do not match.', 'error');
      return;
    }
    if (!token || !email) {
      addToast('Invalid reset link. Please request a new one.', 'error');
      return;
    }

    setSubmitting(true);
    try {
      const res = await fetch('/api/auth/reset-password', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email, token, newPassword }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.message);
      setDone(true);
      addToast(data.message, 'success');
      // Redirect to login after 3 seconds
      setTimeout(() => navigate('/'), 3000);
    } catch (err) {
      addToast(err.message, 'error');
    } finally {
      setSubmitting(false);
    }
  };

  // If missing token/email in URL
  if (!token || !email) {
    return (
      <div className="auth-page">
        <div className="auth-hero">
          <div className="auth-hero-content">
            <img src="/rivitlogo.svg" alt="Rivit" className="auth-hero-logo-img" />
            <h1>Migration Suite</h1>
            <p>A secure client portal by Rivit Technology Partners.</p>
          </div>
        </div>
        <div className="auth-form-panel">
          <div className="auth-form-container">
            <h2>Invalid Reset Link</h2>
            <p className="auth-subtitle">This password reset link is invalid or has expired. Please request a new one.</p>
            <p className="auth-switch" style={{ marginTop: '1.5rem' }}>
              <Link to="/forgot-password">Request New Reset Link</Link>
            </p>
            <p className="auth-switch">
              <Link to="/">← Back to Sign In</Link>
            </p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="auth-page">
      <div className="auth-hero">
        <div className="auth-hero-content">
          <img src="/rivitlogo.svg" alt="Rivit" className="auth-hero-logo-img" />
          <h1>Migration Suite</h1>
          <p>A secure client portal by Rivit Technology Partners. Upload your Lotus Notes DXL exports, and our team will deliver detailed migration analysis and readiness reports — all within a protected, encrypted environment.</p>
          <div className="auth-hero-features">
            <div className="auth-hero-feature">
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><rect x="3" y="11" width="18" height="11" rx="2" ry="2"/><path d="M7 11V7a5 5 0 0 1 10 0v4"/></svg>
              <span>Secure &amp; Encrypted Uploads</span>
            </div>
            <div className="auth-hero-feature">
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/><polyline points="14 2 14 8 20 8"/><line x1="16" y1="13" x2="8" y2="13"/><line x1="16" y1="17" x2="8" y2="17"/></svg>
              <span>Automated DXL Analysis</span>
            </div>
            <div className="auth-hero-feature">
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"/><polyline points="22 4 12 14.01 9 11.01"/></svg>
              <span>Migration Readiness Reports</span>
            </div>
            <div className="auth-hero-feature">
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"/></svg>
              <span>Confidential &amp; Client-Only Access</span>
            </div>
          </div>
        </div>
      </div>
      <div className="auth-form-panel">
        <div className="auth-form-container">
          <h2>Reset Password</h2>
          {!done ? (
            <>
              <p className="auth-subtitle">Choose a new password for <strong>{email}</strong></p>
              <form onSubmit={handleSubmit} className="auth-form">
                <div className="form-card">
                  <div className="form-group">
                    <label htmlFor="newPassword">New Password</label>
                    <input
                      id="newPassword"
                      type="password"
                      placeholder="At least 8 characters"
                      value={newPassword}
                      onChange={(e) => setNewPassword(e.target.value)}
                      autoComplete="new-password"
                      autoFocus
                    />
                  </div>
                  <div className="form-group">
                    <label htmlFor="confirmPassword">Confirm Password</label>
                    <input
                      id="confirmPassword"
                      type="password"
                      placeholder="Re-enter your new password"
                      value={confirmPassword}
                      onChange={(e) => setConfirmPassword(e.target.value)}
                      autoComplete="new-password"
                    />
                  </div>
                </div>
                <button type="submit" className="btn btn-primary btn-full" disabled={submitting}>
                  {submitting ? 'Resetting...' : 'Reset Password'}
                </button>
              </form>
            </>
          ) : (
            <div className="reset-sent-message">
              <div className="reset-sent-icon">
                <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="#16a34a" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14" />
                  <polyline points="22 4 12 14.01 9 11.01" />
                </svg>
              </div>
              <h3>Password Reset Successful</h3>
              <p className="auth-subtitle">
                Your password has been changed. Redirecting you to sign in...
              </p>
            </div>
          )}
          <p className="auth-switch">
            <Link to="/">← Back to Sign In</Link>
          </p>
        </div>
      </div>
    </div>
  );
}
